import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CALC } from '../data/copy';
import ExportPanel from './ExportPanel';

/**
 * The Centurion Field Calculator.
 *
 * variant:
 *   - "full"    centerpiece with Saira-numeric outputs
 *   - "compact" inline narrow version (single column) for hub pages
 *
 * Math (verified in copy.js / data - single source of truth):
 *   Access mode: mats = ceil(km * 477 + corners * 25)
 *
 * Lease mode was retired as part of the business repositioning to a
 * manufacturer-only identity (Centurion does not lease). The access-corridor
 * calc is now the single supported flow. The old `calcLease` helper and
 * `wasteFactor` constant have been dropped from the UI; the backend still
 * accepts the deprecated `waste_factor` boolean as a no-op for backward
 * compatibility with any in-flight clients.
 *
 * Output animation: simple count-up on number changes (no library).
 */

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const toNum = (v, fallback = 0) => {
    const n = parseFloat(v);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
};
const toInt = (v, fallback = 0) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
};

// --- count-up animation hook --------------------------------------------
const useCountUp = (target, duration = 300) => {
    const [v, setV] = useState(target);
    const fromRef = useRef(target);
    const rafRef = useRef(null);

    useEffect(() => {
        const from = fromRef.current;
        const start = performance.now();
        const step = (now) => {
            const elapsed = now - start;
            const t = Math.min(1, elapsed / duration);
            const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
            const cur = from + (target - from) * eased;
            setV(cur);
            if (t < 1) {
                rafRef.current = requestAnimationFrame(step);
            } else {
                fromRef.current = target;
            }
        };
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafRef.current);
    }, [target, duration]);
    return v;
};

const NumberOut = ({ value, suffix = '', decimals = 0 }) => {
    const animated = useCountUp(value);
    const fmt =
        decimals > 0 ? animated.toFixed(decimals) : Math.round(animated).toLocaleString();
    return (
        <span className="font-display font-bold tabular-nums tracking-tight">
            {fmt}
            {suffix ? <span className="text-brand-muted text-sm ml-1">{suffix}</span> : null}
        </span>
    );
};

// --- math (pure, exported for verification in code review) --------------
export const calcAccess = ({ km, corners }) =>
    Math.ceil(toNum(km) * CALC.accessPerKm + toInt(corners) * CALC.accessPerCorner);

// --- input UI ------------------------------------------------------------

const Field = ({ label, suffix, value, onChange, max, type = 'number', step = 'any', testId }) => (
    <label className="block">
        <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">
            {label}
        </span>
        <div className="mt-2 flex items-center border border-brand-hairline bg-brand-elevated transition-colors focus-within:border-brand-gold">
            <input
                type={type}
                inputMode="decimal"
                step={step}
                min="0"
                max={max}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full bg-transparent px-4 py-3 font-display text-2xl font-bold tabular-nums text-brand-text outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            {suffix ? (
                <span className="pr-4 font-sans text-xs uppercase tracking-[0.18em] text-brand-muted">
                    {suffix}
                </span>
            ) : null}
        </div>
    </label>
);

// --- the component -------------------------------------------------------

export const MattingCalculator = ({
    variant = 'full',
    hideCta = false,
    onCalcChange,
    skipAnchorId,
}) => {
    const navigate = useNavigate();

    // access inputs
    const [km, setKm] = useState('1');
    const [corners, setCorners] = useState('0');

    // clamp inputs to limits to prevent silly values
    const kmClamped = clamp(toNum(km), 0, CALC.limits.kmMax);
    const cornersClamped = clamp(toInt(corners), 0, CALC.limits.cornersMax);

    const mats = useMemo(
        () => calcAccess({ km: kmClamped, corners: cornersClamped }),
        [kmClamped, cornersClamped]
    );
    const sqFt = mats * CALC.matAreaSqFt;
    const linearFt = mats * CALC.matLengthFt;
    const trucks = Math.ceil(mats / CALC.matsPerTruck);

    // Live calc broadcast - used by /quote to prefill the form fields in
    // real time as the user edits inputs.
    useEffect(() => {
        if (typeof onCalcChange === 'function') {
            onCalcChange({
                mat_type: 'access',
                total_mats: mats,
                project_type: 'access_road',
                surface_sqft: sqFt,
                linear_ft: linearFt,
                truckloads: trucks,
                calc_state: {
                    mode: 'access',
                    km: kmClamped,
                    corners: cornersClamped,
                    mats,
                    sq_ft: sqFt,
                },
            });
        }
    }, [mats, sqFt, linearFt, trucks, kmClamped, cornersClamped, onCalcChange]);

    const onGetQuote = () => {
        const calculator = {
            mode: 'access',
            km: kmClamped,
            corners: cornersClamped,
            mats,
            sq_ft: sqFt,
        };
        navigate('/quote', {
            state: {
                calculator,
                prefill: {
                    mat_type: 'access',
                    estimated_qty: mats,
                    project_type: 'access_road',
                },
            },
        });
    };

    const isCompact = variant === 'compact';

    return (
        <div
            className={[
                'border border-brand-hairline bg-brand-surface',
                isCompact ? 'p-6' : 'p-6 md:p-10',
            ].join(' ')}
            data-testid="matting-calculator"
        >
            {/* Section header (replaces the retired mode tabs). */}
            <div className="border-b border-brand-hairline pb-4">
                <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-brand-gold">
                    Access Corridor Estimator
                </span>
            </div>

            <div
                className={[
                    'mt-8 grid gap-8',
                    isCompact ? 'grid-cols-1' : 'lg:grid-cols-[1fr_1.1fr] lg:gap-12',
                ].join(' ')}
            >
                {/* Inputs */}
                <div className="space-y-5">
                    <Field
                        label="Distance"
                        suffix="km"
                        value={km}
                        onChange={setKm}
                        max={CALC.limits.kmMax}
                        testId="calc-input-km"
                    />
                    <Field
                        label="Corners / turns"
                        suffix="corners"
                        value={corners}
                        onChange={setCorners}
                        max={CALC.limits.cornersMax}
                        step="1"
                        testId="calc-input-corners"
                    />
                    <p className="font-sans text-xs text-brand-muted leading-relaxed">
                        Mat dimensions locked to standard 8 ft × 14 ft.
                        Custom sizing - contact the mill.
                    </p>
                    <div className="border-t border-brand-hairline pt-4">
                        <p
                            className="font-sans text-xs leading-relaxed text-brand-blue"
                            data-testid="calc-formula"
                        >
                            <span className="font-display uppercase tracking-[0.16em] text-brand-gold">
                                Formula:
                            </span>{' '}
                            {CALC.accessPerKm} × {kmClamped}{' '}
                            + {CALC.accessPerCorner} × {cornersClamped}{' '}
                            = <span className="font-bold text-brand-text">{mats}</span>{' '}
                            mats
                        </p>
                    </div>
                </div>

                {/* Outputs */}
                <div className="border-t border-brand-hairline pt-8 lg:border-l lg:border-t-0 lg:pl-12 lg:pt-0">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-8">
                        <div data-testid="calc-output-mats">
                            <div className="eyebrow !text-brand-muted !text-[10px]">
                                Total Mats
                            </div>
                            <div className="mt-3 text-5xl text-brand-gold">
                                <NumberOut value={mats} />
                            </div>
                        </div>
                        <div data-testid="calc-output-sqft">
                            <div className="eyebrow !text-brand-muted !text-[10px]">
                                Surface Area
                            </div>
                            <div className="mt-3 text-3xl text-brand-blue">
                                <NumberOut value={sqFt} suffix="sq ft" />
                            </div>
                        </div>
                        <div data-testid="calc-output-linear">
                            <div className="eyebrow !text-brand-muted !text-[10px]">
                                Linear Coverage
                            </div>
                            <div className="mt-3 text-3xl text-brand-blue">
                                <NumberOut value={linearFt} suffix="ft" />
                            </div>
                        </div>
                        <div data-testid="calc-output-trucks">
                            <div className="eyebrow !text-brand-muted !text-[10px]">
                                Truckloads (~{CALC.matsPerTruck}/truck)
                            </div>
                            <div className="mt-3 text-3xl text-brand-blue">
                                <NumberOut value={trucks} />
                            </div>
                        </div>
                    </div>

                    {!hideCta && (
                        <button
                            type="button"
                            onClick={onGetQuote}
                            data-testid="calc-cta-quote"
                            className="mt-10 w-full bg-brand-gold px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-[#E5B647]"
                        >
                            Get a Manufacturer Direct Quote →
                        </button>
                    )}
                    {skipAnchorId && (
                        <a
                            href={`#${skipAnchorId}`}
                            data-testid="calc-skip-to-form"
                            className="mt-6 inline-flex items-center gap-2 font-display text-[11px] font-bold uppercase tracking-[0.16em] text-brand-blue transition-colors hover:text-brand-gold lg:hidden"
                        >
                            Skip to form ↓
                        </a>
                    )}
                </div>
            </div>

            {!isCompact && (
                <div className="mt-8">
                    <ExportPanel
                        calcMode="access"
                        matType="access"
                        inputs={{ distance_km: kmClamped, corners: cornersClamped }}
                        derived={{
                            total_mats: mats,
                            surface_sqft: sqFt,
                            truckloads: trucks,
                            linear_ft: linearFt,
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default MattingCalculator;
